<template>
	<div class="w-80 lg:w-64 mr-24 flex flex-col h-full pl-4 lg:pl-0">
		<!-- Refine by header section - sticky on mobile when mobileMode is true -->
		<div :class="{ 'sticky top-0 bg-white z-20': mobileMode }" class="pb-4">
			<h2 class="text-lg font-medium text-ihh-midnight-gray mb-2 pt-4">Refine by</h2>
			<p v-if="Object.keys(selectedFilters).length === 0" class="text-sm text-ihh-slate-gray mb-4">No filters applied</p>
			<div v-else class="mb-4">
				<div v-for="(values, filterName) in selectedFilters" :key="filterName" class="mb-2">
					<div v-for="value in values" :key="filterName + value" class="flex items-center mb-1">
						<button @click="removeFilterValue(filterName, value)" class="flex items-center bg-ihh-gray/40 rounded py-1 px-2 text-xs">
							<span class="mr-1">×</span>
							<span class="font-medium text-ihh-midnight-gray">{{ filterName }}: {{ value?.name || value }}</span>
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Scrollable filters section -->
		<div class="pr-2" style="max-height: 100%">
			<!-- Debug info (will be removed later) -->
			<div v-if="refineByFilters.length === 0" class="text-sm text-red-500 p-2 mb-4 bg-red-100 rounded">No filters available. Check console for details.</div>

			<!-- Filter Sections -->
			<div v-for="filter in refineByFilters" :key="filter.filterName" class="mb-4" :ref="'filter-' + filter.filterName">
				<button @click="toggleFilterSection(filter.filterName)" class="w-full">
					<div class="flex justify-between items-center mb-4">
						<h3 class="font-bold text-sm text-ihh-midnight-gray">{{ filter.filterName }}</h3>
						<div class="text-ihh-midnight-gray">
							<span v-if="isFilterExpanded(filter)">—</span>
							<svg v-else class="w-3" viewBox="30 45 40 25" xmlns="http://www.w3.org/2000/svg">
								<polygon points="50,70 30,50 35,45 50,60 65,45 70,50" class="fill-ihh-midnight-gray" />
							</svg>
						</div>
					</div>
				</button>

				<div v-if="isFilterExpanded(filter)" class="space-y-2">
					<!-- Special case for Price filter -->
					<div v-if="filter.filterName === 'Price'" class="flex space-x-2">
						<input type="text" placeholder="Min." v-model="priceRange.min" class="border rounded w-full p-2 text-sm" />
						<input type="text" placeholder="Max." v-model="priceRange.max" class="border rounded w-full p-2 text-sm" />
						<button @click="applyPriceFilter" class="border border-ihh-salmon hover:bg-ihh-salmon text-ihh-midnight-gray px-4 py-2 rounded text-sm">Update</button>
					</div>
					<!-- Rating filter with stars component -->
					<div v-else-if="filter.filterName === 'Rating'" class="space-y-3">
						<div
							v-for="option in [...filter.options].sort((a, b) => b.name - a.name)"
							:key="option.name"
							:class="['flex items-center cursor-pointer rounded p-1', isRatingSelected(option.name) ? 'bg-ihh-check' : '']"
							@click="toggleRatingFilter(option.name)"
						>
							<div class="flex-grow">
								<Stars :stars="option.name" />
							</div>
							<div class="ml-2">
								<span class="text-ihh-slate-gray text-sm">({{ option.count }})</span>
							</div>
						</div>
					</div>
					<!-- Regular filter options -->
					<template v-else>
						<div v-if="!filter.options || filter.options.length === 0" class="text-ihh-slate-gray text-sm italic">No options available</div>
						<div
							v-else
							v-for="(option, index) in filter.options"
							:key="option.name + (option.id || '')"
							class="flex items-center"
							v-show="filter.filterName !== 'Brand' || index < 10 || showMoreMap[filter.filterName]"
						>
							<label class="flex items-center w-full cursor-pointer">
								<span class="custom-checkbox">
									<input
										:id="option.name"
										type="checkbox"
										:checked="isFilterSelected(filter.filterName, option.name)"
										@change="toggleFilter(filter.filterName, option.name, option?.id)"
									/>
									<span class="checkmark"></span>
								</span>
								<div class="flex-grow w-full flex justify-between">
									<span class="text-ihh-slate-gray text-base ml-2">{{ option.name }}</span>
									<span v-if="option.count" class="text-ihh-slate-gray text-sm ml-2">({{ option.count }})</span>
								</div>
							</label>
						</div>
					</template>

					<!-- Show More button -->
					<template v-if="filter.options && filter.options.length > 12">
						<div v-if="isFilterExpanded(filter)" class="mt-2">
							<button @click="toggleShowMore(filter.filterName)" class="text-ihh-slate-gray text-sm hover:text-ihh-salmon">
								{{ showMoreMap[filter.filterName] ? 'Show Less' : 'Show More' }}
							</button>
						</div>
					</template>
				</div>
			</div>
			<!-- Spacer for bottom padding -->
			<div class="h-12"></div>
		</div>
	</div>
</template>

<style>
/* .filters-scroll {
	scrollbar-width: thin;
	scrollbar-color: #d1d5db transparent;
}

.filters-scroll::-webkit-scrollbar {
	width: 6px;
}

.filters-scroll::-webkit-scrollbar-track {
	background: transparent;
}

.filters-scroll::-webkit-scrollbar-thumb {
	background-color: #d1d5db;
	border-radius: 20px;
} */
</style>

<script>
import Stars from '../../partials/stars.vue'
import axios from 'axios'

export default {
	name: 'RefineBy',
	inject: ['store'],
	components: {
		Stars,
	},
	props: {
		categoryId: {
			type: String,
			required: true,
		},
		refineByFilters: {
			type: Array,
			default: () => [],
		},
		showFilters: {
			type: Boolean,
			default: false,
		},
		mobileMode: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			expandedSections: {},
			showMoreMap: {},
			selectedFilters: {},
			priceRange: {
				min: '',
				max: '',
			},
			selectedRatingValue: 0,
			manuallyCollapsedFilters: [],
			loading: false,
			filterOptions: [],
			activeFilters: {},
			filterUpdateTimeout: null,
			panelOpen: false,
		}
	},
	methods: {
		isFilterExpanded(filter) {
			// If the filter has been manually collapsed, it should be collapsed
			if (this.manuallyCollapsedFilters.includes(filter.filterName)) {
				return false
			}

			// If the filter has collapsedByDefault set to false, it should be expanded
			if (filter.collapsedByDefault === false) {
				return true
			}

			// Otherwise, check the expandedSections object
			return this.expandedSections[filter.filterName] === true
		},
		toggleShowMore(filterName) {
			this.showMoreMap[filterName] = !this.showMoreMap[filterName]
		},
		toggleFilterSection(filterName) {
			const filter = this.refineByFilters.find((f) => f.filterName === filterName)

			// If this filter should be expanded by default and it's currently expanded
			if (filter && filter.collapsedByDefault === false && !this.manuallyCollapsedFilters.includes(filterName)) {
				// Add it to the manually collapsed list
				this.manuallyCollapsedFilters.push(filterName)
			} else if (filter && filter.collapsedByDefault === false) {
				// Remove it from the manually collapsed list
				this.manuallyCollapsedFilters = this.manuallyCollapsedFilters.filter((f) => f !== filterName)
			} else {
				// For normal filters, just toggle the expanded state
				this.expandedSections[filterName] = !this.expandedSections[filterName]
			}
		},
		isFilterSelected(filterName, value) {
			if (!this.selectedFilters[filterName]) return false

			if (filterName === 'Brand') {
				// For Brand filter, check if any object has matching name
				return this.selectedFilters[filterName].some((v) => {
					if (typeof v === 'object' && v.name) {
						return v.name === value
					}
					return v === value
				})
			} else {
				// For other filters, check direct inclusion
				return this.selectedFilters[filterName].includes(value)
			}
		},
		toggleFilter(filterName, value, id) {
			// Toggle the filter selection
			if (this.isFilterSelected(filterName, value)) {
				this.removeFilterValue(filterName, value)
			} else {
				this.addFilterValue(filterName, value, id)
			}
		},
		addFilterValue(filterName, value, id) {
			if (!this.selectedFilters[filterName]) {
				this.selectedFilters[filterName] = []
			}

			// For Brand filter, store both name and id
			if (filterName === 'Brand' && id) {
				this.selectedFilters[filterName].push({ name: value, id: id })
			} else {
				this.selectedFilters[filterName].push(value)
			}

			this.$emit('filter-change', this.selectedFilters)
		},
		removeFilterValue(filterName, value) {
			if (this.selectedFilters[filterName]) {
				// Special handling for Price filter
				if (filterName === 'Price') {
					// Reset price range inputs when removing price filter
					this.priceRange = { min: '', max: '' }
					delete this.selectedFilters[filterName]
					this.applyPriceFilter()
				} else if (filterName === 'Brand') {
					// For Brand filter, filter by name property
					this.selectedFilters[filterName] = this.selectedFilters[filterName].filter((v) => {
						if (typeof v === 'object' && v.name) {
							return v.name !== value.name && v.name !== value
						}
						return v !== value
					})
				} else {
					// For other filters, filter by direct value comparison
					this.selectedFilters[filterName] = this.selectedFilters[filterName].filter((v) => v !== value)
				}

				// If no values left for this filter, remove the entire filter
				if (this.selectedFilters[filterName] && this.selectedFilters[filterName].length === 0) {
					delete this.selectedFilters[filterName]
				}

				// Update activeFilters to match the current selectedFilters
				this.activeFilters = { ...this.selectedFilters }

				// Emit the filter change event
				this.$emit('filter-change', this.selectedFilters)
				console.log('Filter removed, current filters:', this.selectedFilters)
			}
		},
		removeFilter(filterName) {
			if (this.selectedFilters[filterName]) {
				delete this.selectedFilters[filterName]
				this.$emit('filter-change', this.selectedFilters)
			}
		},
		applyPriceFilter() {
			// Remove any existing price filters
			if (this.selectedFilters['Price']) {
				delete this.selectedFilters['Price']
			}

			// Add new price filter if values are provided
			if (this.priceRange.min || this.priceRange.max) {
				let priceValue = ''
				if (this.priceRange.min && this.priceRange.max) {
					priceValue = `$${this.priceRange.min} - $${this.priceRange.max}`
				} else if (this.priceRange.min) {
					priceValue = `$${this.priceRange.min}+`
				} else if (this.priceRange.max) {
					priceValue = `Up to $${this.priceRange.max}`
				}

				this.selectedFilters['Price'] = [priceValue]
			}

			// Update activeFilters with the price filter
			this.activeFilters = { ...this.selectedFilters }

			// // Emit filter change for the UI updates
			// this.$emit('filter-change', this.selectedFilters)

			// Emit price range change for the parent to update its filters.price array
			const min = this.priceRange.min ? parseFloat(this.priceRange.min) : 0
			const max = this.priceRange.max ? parseFloat(this.priceRange.max) : 1000
			this.$emit('price-range-change', { min, max })
		},
		toggleRatingFilter(starValue) {
			// Initialize Rating array if it doesn't exist
			if (!this.selectedFilters['Rating']) {
				this.selectedFilters['Rating'] = []
			}

			// Check if this rating is already selected
			const index = this.selectedFilters['Rating'].indexOf(starValue)

			if (index > -1) {
				// If already selected, remove it
				this.selectedFilters['Rating'].splice(index, 1)

				// If no ratings left, delete the Rating filter
				if (this.selectedFilters['Rating'].length === 0) {
					delete this.selectedFilters['Rating']
				}
			} else {
				// Otherwise, add this rating
				this.selectedFilters['Rating'].push(starValue)
			}

			// Update activeFilters with the rating filter
			this.activeFilters = { ...this.selectedFilters }

			// Emit the filter change event with the updated filters
			this.$emit('filter-change', this.selectedFilters)
			console.log(this.selectedFilters)
		},
		isRatingSelected(starValue) {
			// Check if this rating is in the selected ratings array
			return this.selectedFilters['Rating'] && this.selectedFilters['Rating'].includes(starValue)
		},
		configureFilterDefaults() {
			// Initialize expanded sections and showMore state
			this.expandedSections = {} // Clear any existing values
			this.showMoreMap = {} // Clear existing values
			this.manuallyCollapsedFilters = [] // Clear manually collapsed filters

			// Initialize default expanded state for all filters
			if (this.refineByFilters && this.refineByFilters.length > 0) {
				this.refineByFilters.forEach((filter) => {
					// Only set expandedSections for filters that should be collapsed by default
					if (filter.collapsedByDefault !== false) {
						this.expandedSections[filter.filterName] = false
					}

					// Initialize showMoreMap for all filters
					this.showMoreMap[filter.filterName] = false
				})
			}
		},
		sortFilters() {
			if (!this.refineByFilters || !Array.isArray(this.refineByFilters)) {
				return
			}

			// Create a custom sorting function for the filters
			const getPriority = (filterName) => {
				// Set priority based on filter name
				switch (filterName) {
					case 'Brand':
						return 1
					case 'Price':
						return 2
					case 'Rating':
						return 3
					default:
						return 4 // All other filters
				}
			}

			// Sort the array in place
			this.refineByFilters.sort((a, b) => {
				const priorityA = getPriority(a.filterName)
				const priorityB = getPriority(b.filterName)

				// If priorities are different, sort by priority
				if (priorityA !== priorityB) {
					return priorityA - priorityB
				}

				// If priorities are the same (both are 'other filters'), sort alphabetically
				return a.filterName.localeCompare(b.filterName)
			})
		},
		closeMobileFilters() {
			this.panelOpen = false
		},
		emitClose() {
			this.$emit('update:showFilters', false)
		},
	},

	watch: {
		refineByFilters: {
			handler(newFilters) {
				if (newFilters && newFilters.length) {
					// Sort the filters according to the specified order
					this.sortFilters()
					// Configure filter defaults after sorting
					// this.configureFilterDefaults()
				}
			},
			immediate: true, // Run immediately when component is created
			deep: true, // Watch for deep changes in the array
		},

		// Watch for changes in categoryId to refetch filter options
		categoryId: {
			handler(newCategoryId) {
				if (newCategoryId) {
					// Reset filters when category changes
					this.selectedFilters = {}
					this.activeFilters = {}
					this.priceRange = { min: '', max: '' }
					this.selectedRatingValue = 0
				}
			},
			immediate: true,
		},

		// Watch for changes in selected filters
		selectedFilters: {
			handler(newFilters) {
				this.$emit('filter-change', newFilters)
			},
			deep: true,
		},
		showFilters(val) {
			if (val) this.panelOpen = true
		},
	},
}
</script>

<style scoped>
/* Checkbox styling */
.custom-checkbox {
	position: relative;
	display: inline-block;
	width: 16px;
	height: 16px;
	border-radius: 3px;
	border: 1px solid #d1d5db;
	background-color: transparent;
	cursor: pointer;
	margin-right: 8px;
	overflow: hidden;
}

.custom-checkbox input {
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
}

.checkmark {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.custom-checkbox input:checked ~ .checkmark {
	background-color: #e59789;
	border-color: #2563eb;
}

.custom-checkbox input:checked ~ .checkmark::after {
	content: '';
	position: absolute;
	width: 6px;
	height: 10px;
	border: solid white;
	border-width: 0 2px 2px 0;
	transform: rotate(45deg);
	margin-bottom: 2px;
}
</style>
